import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  CreateScheduleRequest,
  Schedule,
  ScheduleType,
  UpdateScheduleRequest,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  DateRangeField,
  FormRow,
  Modal,
  ModalControlModalProps,
  RadioField,
  Text,
  TextField,
  TimeRangeField,
  useAlert,
  useForm,
} from '@frontend/design-system';
import { ResponsiveModalFooter } from '../../components/settings';
import { usePastDateBanner } from '../../hooks/use-past-date-banner';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { getRRuleStringFromDateRange } from '../../rrule-helper';
import { ManagePractitionerV3TrackingIds } from '../../trackingIds/manage-practitioner-v3-tracking-ids';
import { ExceptionListItem, ExceptionStatus } from '../../types';
import {
  calculateDuration,
  findOverrideIndexById,
  generateExceptionId,
  getLabelWithTimezoneAbbr,
  getMaxCalendarDate,
} from '../../utils';
import { getDateString } from '../../utils/date-helpers';

type CreateAppointmentTypeModalType = {
  locationId: string;
  entityId: string;
  entitySchedule?: Schedule;
  exceptionDetails?: Partial<ExceptionListItem>;
  modalProps: ModalControlModalProps;
  entityType: ScheduleType.PROVIDER | ScheduleType.LOCATION;
};

export const CreateExceptionModal = ({
  locationId,
  entityId,
  entitySchedule,
  modalProps,
  exceptionDetails = {},
  entityType,
}: CreateAppointmentTypeModalType) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const { locationTimezoneAbbr } = useLocationTimezone(locationId);
  const locationTimezone = getLocationTimeZone(locationId);

  const isEdit = !isEmpty(exceptionDetails);
  const entityTypeName = entityType === ScheduleType.PROVIDER ? t('Provider') : t('Office');

  const createScheduleMutation = SchedulerV3.Mutations.useCreateScheduleMutation({
    options: {
      onSuccess: () => {
        alert.success(t('{{entityTypeName}} exception created successfully', { entityTypeName }));
        handleClose();
      },
      onError: () => {
        alert.error(t('Failed to create {{entityTypeName}} exception', { entityTypeName }));
      },
    },
  });

  const updateScheduleMutation = SchedulerV3.Mutations.useUpdateScheduleMutation({
    options: {
      onSuccess: () => {
        alert.success(t('{{entityTypeName}} exception updated successfully', { entityTypeName }));
        handleClose();
      },
      onError: () => {
        alert.error(t('Failed to update {{entityTypeName}} exception', { entityTypeName }));
      },
    },
  });

  const { formProps, getFieldProps, reset, seedValues, isComplete, changedValues, values } = useForm({
    fields: {
      name: {
        type: 'text',
        required: true,
      },
      status: {
        type: 'radio',
        required: true,
      },
      dateRange: {
        type: 'dateRange',
        required: true,
        maxDate: getMaxCalendarDate(),
        validator: ({ value }) => {
          const startDate = dayjs(value?.[0]);
          const endDate = dayjs(value?.[1]);
          if (startDate.isValid() && endDate.isValid() && endDate.isBefore(startDate)) {
            return t('End date must be after the start date');
          }
          return '';
        },
      },
      timeRange: {
        type: 'timeRange',
        interval: 15,
        joiningText: 'to',
      },
      isAllDay: {
        type: 'checkbox',
      },
    },
    computeChangedValues: true,
    onSubmit: async (values) => {
      const { recurrenceRules } = entitySchedule || {};

      const newOverride = {
        availabilities: recurrenceRules?.override?.availabilities || [],
        un_availabilities: recurrenceRules?.override?.unAvailabilities || [],
      };

      if (values.isAllDay) {
        values.timeRange = ['00:00:00', '23:59:00'];
      }

      const rrule = getRRuleStringFromDateRange(
        {
          startDate: values.dateRange?.[0],
          endDate: values.dateRange?.[1],
          startTime: values.timeRange?.[0] ?? '',
        },
        locationTimezone
      );
      const [startTime, endTime] = values.timeRange ?? [];
      const duration = calculateDuration(startTime, endTime);

      // Generate IDs only for new exceptions
      const exceptionEntry = {
        id: isEdit ? exceptionDetails.id : generateExceptionId(),
        name: values.name,
        rrule,
        duration,
      };

      if (isEdit) {
        const isStatusChangedToAvailable =
          exceptionDetails.status === ExceptionStatus.Unavailable && values.status === ExceptionStatus.Available;
        const isStatusChangedToUnavailable =
          exceptionDetails.status === ExceptionStatus.Available && values.status === ExceptionStatus.Unavailable;

        if (isStatusChangedToUnavailable) {
          // Remove from availabilities and add to unavailabilities
          const index = findOverrideIndexById(newOverride.availabilities, exceptionDetails.id);

          if (index !== -1) {
            newOverride.availabilities.splice(index, 1);
            newOverride.un_availabilities.push(exceptionEntry);
          }
        } else if (isStatusChangedToAvailable) {
          // Remove from unavailabilities and add to availabilities
          const index = findOverrideIndexById(newOverride.un_availabilities, exceptionDetails.id);

          if (index !== -1) {
            newOverride.un_availabilities.splice(index, 1);
            newOverride.availabilities.push(exceptionEntry);
          }
        } else if (exceptionDetails.status === ExceptionStatus.Available) {
          const availabilityIndex = findOverrideIndexById(newOverride.availabilities, exceptionDetails.id);

          if (availabilityIndex !== -1) {
            newOverride.availabilities.splice(availabilityIndex, 1, exceptionEntry);
          }
        } else {
          const unAvailabilityIndex = findOverrideIndexById(newOverride.un_availabilities, exceptionDetails.id);

          if (unAvailabilityIndex !== -1) {
            newOverride.un_availabilities.splice(unAvailabilityIndex, 1, exceptionEntry);
          }
        }
      } else {
        if (values.status === ExceptionStatus.Available) {
          newOverride.availabilities.push(exceptionEntry);
        } else {
          newOverride.un_availabilities.push(exceptionEntry);
        }
      }

      const payload: UpdateScheduleRequest | CreateScheduleRequest = {
        id: entityId,
        locationId,
        type: entityType,
        recurrenceRules: {
          ...recurrenceRules,
          override: newOverride,
        },
      };

      if (isEdit) {
        updateScheduleMutation.mutate(payload);
      } else {
        createScheduleMutation.mutate(payload);
      }
    },
  });

  const handleSave = (event?: React.FormEvent<Element>) => {
    if (event) {
      formProps.onSubmit(event);
    }
  };

  const handleClose = () => {
    reset();
    modalProps.onClose();
  };

  /**
   * FIXME: This is a workaround to check if the date range value has changed on edit.
   * This is needed because the `changedValues` from useForm is returning false if endDate is changed to the startDate.
   */
  const isDateRangeValueChangedOnEdit = useMemo(() => {
    if (isEdit) {
      const initialStartDate = dayjs(exceptionDetails.startDateTime).date();
      const initialEndDate = dayjs(exceptionDetails.endDateTime).date();
      const formStartDate = dayjs(values.dateRange?.[0]).date();
      const formEndDate = dayjs(values.dateRange?.[1]).date();

      return initialStartDate !== formStartDate || initialEndDate !== formEndDate;
    }

    return false;
  }, [exceptionDetails, values.dateRange]);

  /**
   * FIXME: This is a workaround to check if the form is complete.
   * This is needed because the `isComplete` from useForm is returning true if form is re-open after filling details for `dateRange` field.
   * Also, we need to conditionally check for dateRange and timeRange based on isAllDay value.
   */
  const isFormComplete = useMemo(() => {
    return (
      isComplete &&
      (values.dateRange?.[0] || values.dateRange?.[1]) &&
      (values.isAllDay || (!values.isAllDay && values.timeRange?.[0] && values.timeRange?.[1]))
    );
  }, [isComplete, values]);

  // Seed form values on edit
  useEffect(() => {
    if (isEdit) {
      const startDate = dayjs.utc(exceptionDetails.startDateTime);
      const endDate = dayjs.utc(exceptionDetails.endDateTime);

      const startTime = startDate.format('HH:mm:ss');
      const endTime = startDate.add(exceptionDetails.duration ?? 0, 'minutes').format('HH:mm:ss');

      seedValues({
        name: exceptionDetails?.name,
        status: exceptionDetails?.status,
        dateRange: [startDate.format('MM/DD/YYYY'), endDate.format('MM/DD/YYYY')],
        timeRange: [startTime, endTime],
        isAllDay: startTime === '00:00:00' && endTime === '23:59:00',
      });
    }
  }, [isEdit, exceptionDetails]);

  // Update isAllDay field value to false when status is changed to Available
  useEffect(() => {
    if (values.status === 'Available') {
      seedValues({ isAllDay: false });
    }
  }, [values.status]);

  const { pastDateBannerComponent } = usePastDateBanner({
    initialDate: getDateString(exceptionDetails?.startDateTime),
    currentDate: values.dateRange?.[0],
    locationId: locationId,
  });

  const dateRangeField = getFieldProps('dateRange');
  const dateRangeError = !!dateRangeField.value?.[0] && dateRangeField.value?.[1] && dateRangeField.error;

  return (
    <Modal.Responsive {...modalProps} minWidth={420} maxWidth={420} onClose={handleClose}>
      <Modal.Header onClose={handleClose}>{isEdit ? t('Edit Exception') : t('New Exception')}</Modal.Header>
      <Modal.Body>
        <form {...formProps}>
          <FormRow>
            <TextField {...getFieldProps('name')} label={t('Name of Exception*')} />
          </FormRow>
          <FormRow>
            <RadioField {...getFieldProps('status')} name='status'>
              <RadioField.Radio value={'Available'}>{t('Available')}</RadioField.Radio>
              <RadioField.Radio value={'Unavailable'}>{t('Unavailable')}</RadioField.Radio>
            </RadioField>
          </FormRow>
          <FormRow>
            <div>
              <DateRangeField
                {...getFieldProps('dateRange')}
                label={t('Exception Date')}
                labelMin={getLabelWithTimezoneAbbr(t('Start Date'), locationTimezoneAbbr)}
                labelMax={getLabelWithTimezoneAbbr(t('End Date'), locationTimezoneAbbr)}
                css={styles.dateRangeField}
              />
              {/* NOTE: This is a workaround to show error for the date range field component as the component doesn't show validation error for combined fields */}
              {!!dateRangeError && (
                <Text size='small' color='error' css={{ paddingTop: theme.spacing(1) }}>
                  {dateRangeError}
                </Text>
              )}
            </div>
          </FormRow>
          {/* Only show for UnAvailable type exceptions as availability can't be for full day */}
          {values.status === 'Unavailable' && (
            <FormRow>
              <CheckboxField {...getFieldProps('isAllDay')} label={t('All-Day')} />
            </FormRow>
          )}
          <FormRow>
            <div
              css={css`
                .exception-date-range {
                  justify-content: space-between;
                }
              `}
            >
              <TimeRangeField
                {...getFieldProps('timeRange')}
                label={t('Exception Time')}
                startLabel={getLabelWithTimezoneAbbr(t('Start Time'), locationTimezoneAbbr)}
                endLabel={getLabelWithTimezoneAbbr(t('End Time'), locationTimezoneAbbr)}
                disabled={values.isAllDay}
                className='exception-date-range'
                css={styles.timeRangeField}
              />
            </div>
          </FormRow>
        </form>
        {pastDateBannerComponent}
      </Modal.Body>
      <ResponsiveModalFooter
        primary={{
          label: t('Save'),
          disabled: !isFormComplete || (isEdit && !changedValues && !isDateRangeValueChangedOnEdit),
          loading: createScheduleMutation.isLoading || updateScheduleMutation.isLoading,
          onClick: handleSave,
          trackingId: ManagePractitionerV3TrackingIds.createExceptionModalSaveBtn,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: handleClose,
          disabled: createScheduleMutation.isLoading || updateScheduleMutation.isLoading,
          trackingId: ManagePractitionerV3TrackingIds.createExceptionModalCancelBtn,
        }}
      />
    </Modal.Responsive>
  );
};

const styles = {
  dateRangeField: css`
    > div {
      display: flex;
      flex-wrap: wrap;
      gap: ${theme.spacing(2)};
      margin: 0;
      > div:nth-of-type(1),
      > div:nth-of-type(2) {
        margin: 0;
        flex-basis: 175px;
        flex: 1 1 175px;
        > div {
          max-width: none;
        }
      }
    }
  `,
  timeRangeField: css`
    display: flex;
    gap: ${theme.spacing(1)};
    row-gap: ${theme.spacing(2)};
    flex-wrap: wrap;
    > div {
      flex: 1 0 170px;
      > div {
        width: auto;
        min-width: 170px;
        margin: 0;
      }
    }
  `,
};
